(() => {
  const DESKTOP_MEDIA_QUERY = '(min-width: 990px)';
  const MOBILE_IMAGE_WARMUP_MARGIN = '450px 0px';
  const DESKTOP_IMAGE_WARMUP_MARGIN = '280px 0px';

  const prepareArrowDrawPaths = (section) => {
    const arrows = section.querySelectorAll('.pdp-magic-behind-playwall__arrow svg');

    arrows.forEach((svg) => {
      const isTailPath = (path) => {
        const partName = [
          path.id,
          path.getAttribute('class'),
          path.parentElement?.id,
          path.parentElement?.getAttribute('class'),
        ].filter(Boolean).join(' ');

        return /(^|\s|-)tail(-|\s|$)|(^|\s|-)head(-|\s|$)/i.test(partName);
      };

      const paths = [...svg.querySelectorAll('path, polygon, polyline, line, circle, ellipse')]
        .map((path) => {
          if (typeof path.getTotalLength !== 'function') return null;

          return {
            path,
            length: path.getTotalLength(),
            isTail: isTailPath(path),
          };
        })
        .filter(Boolean);

      if (!paths.length) return;

      const drawableMainPaths = paths.filter(({ isTail }) => !isTail);
      const mainPath = (drawableMainPaths.length ? drawableMainPaths : paths).reduce((longest, current) => (
        current.length > longest.length ? current : longest
      ), paths[0])?.path;

      paths.forEach(({ path, length, isTail }) => {
        const stroke = path.getAttribute('stroke');
        const fill = path.getAttribute('fill');
        const startPoint = path.getPointAtLength(0);
        const endPoint = path.getPointAtLength(length);
        const drawOffset = startPoint.x > endPoint.x ? length : -length;

        if (isTail) {
          path.classList.remove('draw-path');
          path.classList.add('draw-arrow-tail');
        } else if (!path.classList.contains('draw-path') && !path.classList.contains('draw-arrow')) {
          path.classList.add(path === mainPath ? 'draw-path' : 'draw-arrow-tail');
        } else if (path.classList.contains('draw-arrow')) {
          path.classList.add('draw-arrow-tail');
        }

        if (!stroke || stroke === 'none') {
          path.setAttribute('stroke', fill && fill !== 'none' ? fill : 'currentColor');
        }

        if (!path.getAttribute('stroke-width')) {
          path.setAttribute('stroke-width', '1.5');
        }

        path.setAttribute('fill', 'none');
        path.setAttribute('stroke-linecap', path.getAttribute('stroke-linecap') || 'round');
        path.setAttribute('stroke-linejoin', path.getAttribute('stroke-linejoin') || 'round');

        path.style.strokeDasharray = length;
        path.style.strokeDashoffset = drawOffset;
        path.style.setProperty('--pdp-magic-draw-length', length);
        path.style.setProperty('--pdp-magic-draw-offset', drawOffset);
      });
    });
  };

  const initMagicBehindPlaywall = (section) => {
    if (!section || section.dataset.magicBehindPlaywallInitialized === 'true') return;

    section.dataset.magicBehindPlaywallInitialized = 'true';
    prepareArrowDrawPaths(section);

    let hasPlayed = false;
    let scrollStopTimer;
    const desktopMediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const centerImages = [...section.querySelectorAll('.pdp-magic-behind-playwall__center-stage img')];

    const warmCenterImages = () => {
      centerImages.forEach((image) => {
        image.loading = 'eager';
        image.fetchPriority = 'high';

        if (typeof image.decode === 'function' && !image.complete) {
          image.decode().catch(() => {});
        }
      });
    };

    const playAnimation = () => {
      hasPlayed = true;
      section.classList.add('pdp-magic-behind-playwall--animated');
    };

    const resetAnimation = () => {
      hasPlayed = false;
      section.classList.remove('pdp-magic-behind-playwall--animated');
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      warmCenterImages();
      playAnimation();
      return;
    }

    if (!('IntersectionObserver' in window)) {
      warmCenterImages();
      playAnimation();
      return;
    }

    const imageWarmupObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        warmCenterImages();
        imageWarmupObserver.disconnect();
      });
    }, {
      rootMargin: desktopMediaQuery.matches ? DESKTOP_IMAGE_WARMUP_MARGIN : MOBILE_IMAGE_WARMUP_MARGIN,
    });

    imageWarmupObserver.observe(section);

    const getTriggerConfig = () => {
      const isDesktop = desktopMediaQuery.matches;

      return {
        visibleRatio: isDesktop ? 0.42 : 0.28,
        viewportCoverage: isDesktop ? 0.5 : 0.42,
        topOffsetRatio: isDesktop ? 0.52 : 0.58,
      };
    };

    const getSectionVisibility = () => {
      const rect = section.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
      const visibleHeight = Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
      const visibleWidth = Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0);
      const visibleArea = Math.max(0, visibleHeight) * Math.max(0, visibleWidth);
      const sectionArea = Math.max(1, rect.height * rect.width);
      const visibleRatio = visibleArea / sectionArea;
      const visibleHeightRatio = Math.max(0, visibleHeight) / Math.max(1, rect.height);
      const { visibleRatio: minVisibleRatio, viewportCoverage, topOffsetRatio } = getTriggerConfig();
      const sectionFillsViewport = rect.top <= 0 && rect.bottom >= viewportHeight * viewportCoverage;
      const reachedLeadInZone = rect.top <= viewportHeight * topOffsetRatio && rect.bottom >= viewportHeight * 0.5;
      const isReady = visibleRatio >= minVisibleRatio || visibleHeightRatio >= viewportCoverage || sectionFillsViewport || reachedLeadInZone;
      const isOut = rect.bottom <= 0 || rect.top >= viewportHeight || visibleRatio <= 0.05;

      return { isReady, isOut };
    };

    const updateAnimationState = () => {
      const { isReady, isOut } = getSectionVisibility();

      window.clearTimeout(scrollStopTimer);

      if (isOut) {
        resetAnimation();
        return;
      }

      if (hasPlayed) return;

      if (isReady) {
        warmCenterImages();
        playAnimation();
      }
    };

    const updateAnimationStateImmediately = () => {
      window.clearTimeout(scrollStopTimer);

      const { isReady, isOut } = getSectionVisibility();

      if (isOut) {
        resetAnimation();
      } else if (isReady && !hasPlayed) {
        warmCenterImages();
        playAnimation();
      }
    };

    updateAnimationState();
    window.addEventListener('scroll', updateAnimationState, { passive: true });
    window.addEventListener('resize', updateAnimationStateImmediately);
  };

  const initAllMagicBehindPlaywallSections = () => {
    document.querySelectorAll('.pdp-magic-behind-playwall[data-section-id]').forEach(initMagicBehindPlaywall);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllMagicBehindPlaywallSections);
  } else {
    initAllMagicBehindPlaywallSections();
  }

  document.addEventListener('shopify:section:load', initAllMagicBehindPlaywallSections);
})();
